import { ExternalLink } from "lucide-react";

const SECTION_PATTERNS = [
  { regex: /^\*?\*?❓?\s*HELP ME BUILD|HELP ME FIND|HELP ME IDENTIFY|QUESTION FOR YOU/i, type: "question" },
  { regex: /^\*?\*?🎯?\s*PROSPECT|^LEAD|^FOUND/i, type: "prospect" },
  { regex: /^\*?\*?⚠️?\s*COMPETITIVE|^COMPETITOR|^THREAT|^DEFENSIVE|^MONITOR/i, type: "threat" },
  { regex: /^\*?\*?💡?\s*WHY (THIS|IT) MATTERS|^INSIGHT|^STRATEGY INSIGHT|^INTEL|^SIGNIFICANCE|^THE GAP|^THE NEWS|^NEWS:/i, type: "insight" },
  { regex: /^\*?\*?⚡?\s*IMMEDIATE ACTION|^ACTION|^NEXT STEP|^FIX|^SALES ANGLE|^EXPECTED IMPACT/i, type: "action" },
  { regex: /^\*?\*?📊?\s*MARKET|^RESEARCH|^DATA|^SOURCE/i, type: "research" },
  { regex: /^\*?\*?🛡️?\s*STRATEGY|^RECOMMENDATION/i, type: "strategy" },
];

const isHeading = (line) => line.length <= 90 && SECTION_PATTERNS.some((p) => p.regex.test(line));

// Splits AI text into titled sections keyed off the known heading patterns.
export const parseInsightSections = (text) => {
  const sections = [];
  let current = null;
  let content = [];
  const push = () => current && sections.push({ ...current, content: content.join("\n").trim() });

  text.split("\n").forEach((line) => {
    const cleanLine = line.replace(/^\*\*|\*\*$/g, "").trim();
    if (isHeading(cleanLine)) {
      push();
      current = { title: cleanLine.replace(/^[❓🎯⚠️💡⚡📊🛡️]\s*/, "").replace(/:$/, ""), type: SECTION_PATTERNS.find((p) => p.regex.test(cleanLine)).type };
      content = [];
      return;
    }
    if (!cleanLine) return;
    const processed = cleanLine.replace(/^\*\s+/, "").replace(/^•\s*/, "").replace(/\*\*/g, "");
    if (processed) content.push(processed);
  });
  push();

  if (sections.length === 0) {
    return [{ title: null, content: text.replace(/\*\*/g, "").replace(/^\*\s+/gm, "• "), type: "default" }];
  }
  return sections;
};

const URL_RE = /(https?:\/\/[^\s<>"')\]]+)/g;
const hostOf = (url) => { try { return new URL(url).hostname.replace(/^www\./, ""); } catch { return url; } };

// Renders a line of text with every URL turned into a link (label = domain).
export const Linkified = ({ text }) => {
  const parts = text.split(URL_RE);
  return parts.map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a key={`${part}-${i}`} href={part.replace(/[.,;:]+$/, "")} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-1 text-ism-teal hover:underline break-all" data-testid="insight-link">
        <ExternalLink className="h-3 w-3 flex-shrink-0" />{hostOf(part)}
      </a>
    ) : (
      <span key={`t-${i}`}>{part}</span>
    )
  );
};

// Same visual language as the lead card's expanded details: teal uppercase section label, gray body, teal links.
export const FormattedInsights = ({ content, onInteraction }) => {
  if (!content) return null;
  return (
    <div className="space-y-4" data-testid="formatted-insights">
      {parseInsightSections(content).map((section, idx) => (
        <div key={`${idx}-${section.title || ""}`} className="rounded-lg border border-white/10 bg-white/[0.03] p-4 hover:border-ism-teal/30 transition-colors"
          onClick={onInteraction} data-testid={`insight-section-${idx}`}>
          {section.title && <h4 className="text-xs font-medium text-ism-teal uppercase tracking-wider mb-2">{section.title}</h4>}
          <div className="text-sm text-gray-300 leading-relaxed space-y-1.5">
            {section.content.split("\n").map((line, lineIdx) => (
              <p key={`${lineIdx}-${line.slice(0, 24)}`} className="flex items-start gap-2">
                <span className="text-ism-teal mt-0.5">•</span>
                <span className="min-w-0"><Linkified text={line} /></span>
              </p>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
